import React, { useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useTranslation } from "@/lib/i18n";

/**
 * CompanyProfileAccordion — Phase 8.LP.1 + Phase 8.LP.POLISH
 * -----------------------------------------------------------
 * Optional, collapsed-by-default "About this company" section.
 *
 * Phase 8.LP.POLISH changes:
 *   * UI labels are now localized via ``useTranslation`` — German
 *     users see "Sektor" / "Branche" / "Börse" etc. Fallback to
 *     English is automatic (i18n index handles it).
 *   * The FMP company description body is NEVER translated —
 *     only the surrounding labels are. No LLM call, no
 *     translated cache — this is a deliberate product decision.
 *   * Long descriptions are visually clamped to a manageable
 *     preview length with a "Show more" / "Show less" toggle
 *     (client-side, no re-fetch). Improves the "wall-of-text"
 *     feel of ticker profiles that carry FMP's full multi-
 *     paragraph description.
 *
 * Empty-row handling is unchanged: rows without a value are
 * omitted; the accordion itself is hidden entirely when there is
 * neither a description NOR any populated profile field.
 */

const DESCRIPTION_PREVIEW_CHARS = 280;

function formatMarketCap(v) {
  if (typeof v !== "number" || !isFinite(v) || v <= 0) return null;
  const abs = Math.abs(v);
  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
  return v.toLocaleString();
}

function formatEmployees(v) {
  if (typeof v !== "number" || !isFinite(v) || v <= 0) return null;
  return v.toLocaleString();
}

function ensureUrl(v) {
  if (typeof v !== "string") return null;
  const s = v.trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+/i.test(s)) return `https://${s}`;
  return null;
}

/** Description with a graceful "Show more / Show less" clamp.
 *
 *  We use a character-count preview rather than a CSS
 *  ``line-clamp`` because we want the toggle text ("Show more"
 *  / "Show less") to also localize and because clamp-based
 *  layouts sometimes flash on hydration.
 */
function ClampedDescription({ text, testIdPrefix, t }) {
  const [expanded, setExpanded] = useState(false);
  const needsClamp = typeof text === "string" && text.length > DESCRIPTION_PREVIEW_CHARS + 40;
  const preview = needsClamp && !expanded
    ? `${text.slice(0, DESCRIPTION_PREVIEW_CHARS).trimEnd()}\u2026`
    : text;
  return (
    <div className="space-y-2">
      <div
        className="text-[10.5px] uppercase tracking-[0.14em] text-muted-foreground"
        data-testid={`${testIdPrefix}-overview-label`}
      >
        {t("analyze.profile.overview_label")}
      </div>
      <p
        className="text-sm leading-relaxed text-muted-foreground max-w-prose whitespace-pre-line"
        data-testid={`${testIdPrefix}-description`}
      >
        {preview}
      </p>
      {needsClamp && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-medium text-primary hover:underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
          data-testid={`${testIdPrefix}-description-toggle`}
        >
          {expanded ? t("analyze.profile.show_less") : t("analyze.profile.show_more")}
        </button>
      )}
    </div>
  );
}

export function CompanyProfileAccordion({ identity, className = "" }) {
  const { t } = useTranslation();
  const rows = useMemo(() => {
    if (!identity) return [];
    const out = [];
    const push = (label, value, opts = {}) => {
      if (value === null || value === undefined || value === "") return;
      out.push({ label, value, ...opts });
    };
    push(t("analyze.profile.row_sector"), identity.sector);
    push(t("analyze.profile.row_industry"), identity.industry);
    push(t("analyze.profile.row_exchange"), identity.exchange_short || identity.exchange);
    push(t("analyze.profile.row_country"), identity.country);
    push(t("analyze.profile.row_currency"), identity.currency);
    push(t("analyze.profile.row_ceo"), identity.ceo);
    push(t("analyze.profile.row_employees"), formatEmployees(identity.employees));
    push(t("analyze.profile.row_market_cap"), formatMarketCap(identity.market_cap));
    const websiteUrl = ensureUrl(identity.website);
    if (websiteUrl) {
      out.push({
        label: t("analyze.profile.row_website"),
        value: websiteUrl.replace(/^https?:\/\//i, ""),
        href: websiteUrl,
      });
    }
    return out;
  }, [identity, t]);

  const hasDescription =
    typeof identity?.description === "string" &&
    identity.description.trim().length > 20;
  // Hide the accordion entirely only when we have absolutely
  // nothing meaningful to show — even a single profile row (e.g.
  // Sector) is enough to justify rendering. Explicitly does NOT
  // require a description any more (spec: "Should not hide just
  // because description is missing if other useful fields exist").
  if (rows.length === 0 && !hasDescription) return null;

  return (
    <div className={className} data-testid="company-profile-accordion">
      <Accordion type="single" collapsible>
        <AccordionItem value="profile" className="border-border/70">
          <AccordionTrigger
            className="text-sm font-medium"
            data-testid="company-profile-accordion-trigger"
          >
            {t("analyze.profile.accordion_title")}
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-5" data-testid="company-profile-accordion-content">
              {hasDescription && (
                <ClampedDescription
                  text={identity.description}
                  testIdPrefix="company-profile"
                  t={t}
                />
              )}
              {rows.length > 0 && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {rows.map((r) => (
                    <div
                      key={r.label}
                      className="flex items-baseline justify-between gap-4 border-b border-border/40 pb-1 last:border-none"
                      data-testid={`company-profile-row-${(r.label || "").toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <dt className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
                        {r.label}
                      </dt>
                      <dd className="text-sm text-foreground text-right truncate max-w-[65%]">
                        {r.href ? (
                          <a
                            href={r.href}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="underline underline-offset-2 hover:text-primary transition-colors"
                            data-testid={`company-profile-${(r.label || "").toLowerCase()}-link`}
                          >
                            {r.value}
                          </a>
                        ) : (
                          r.value
                        )}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

export default CompanyProfileAccordion;
